import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import {
  ACTIVITY_RECOMMENDATION_CATALOG,
  ACTIVITY_RECOMMENDATION_THRESHOLDS,
} from "./utils/activityDetailAnalysis";

const SEVERITY_STYLES = {
  high: "bg-[#FFE2E5] text-[#B42318]",
  medium: "bg-[#FFF4DE] text-[#8A5A00]",
  watch: "bg-[#F2F4F7] text-[#475467]",
};

const SEVERITY_LABELS = {
  high: "High impact",
  medium: "Medium impact",
  watch: "To monitor",
};

const THEMES = [
  {
    id: "filling",
    scopes: ["filling"],
    titleKey: "activityRecoGuide.theme.filling.title",
    descriptionKey: "activityRecoGuide.theme.filling.description",
    thresholdKeys: [
      {
        labelKey: "activityRecoGuide.thresholdSubOptimal",
        valueKey: "SUB_OPTIMAL_FILLING",
      },
      {
        labelKey: "activityRecoGuide.thresholdObjective",
        valueKey: "FILLING_OBJECTIVE",
      },
      {
        labelKey: "activityRecoGuide.thresholdLate",
        valueKey: "LATE_OVERFLOW",
      },
    ],
  },
  {
    id: "sorting",
    scopes: ["sorting"],
    titleKey: "activityRecoGuide.theme.sorting.title",
    descriptionKey: "activityRecoGuide.theme.sorting.description",
    thresholdKeys: [],
    contaminantsBand: true,
  },
  {
    id: "followUp",
    scopes: ["followUp"],
    titleKey: "activityRecoGuide.theme.followUp.title",
    descriptionKey: "activityRecoGuide.theme.followUp.description",
    thresholdKeys: [],
  },
  {
    id: "compaction",
    scopes: ["compaction"],
    titleKey: "activityRecoGuide.theme.compaction.title",
    descriptionKey: "activityRecoGuide.theme.compaction.description",
    thresholdKeys: [],
  },
];

const ImpactBadge = ({ severity, t }) => (
  <span
    className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap ${
      SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.watch
    }`}
  >
    {t(SEVERITY_LABELS[severity] ?? SEVERITY_LABELS.watch)}
  </span>
);

const ThresholdPill = ({ children }) => (
  <span className="inline-flex items-center rounded-md bg-[#F2F4F7] px-2.5 py-1 text-xs text-gray-700">
    {children}
  </span>
);

const RecommendationTypeCard = ({ row, t }) => (
  <div className="flex flex-col gap-3 border border-gray-300 rounded-xl p-4 bg-white">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="flex flex-col gap-0.5 min-w-0">
        <h4 className="m-0 text-sm font-bold text-[#222222]">
          {row.scenarioTitle}
        </h4>
        <p className="m-0 text-sm text-gray-600">{row.scenarioDescription}</p>
      </div>
      <ImpactBadge severity={row.severity} t={t} />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
          {t("activityRecoGuide.whenLabel")}
        </span>
        <span className="text-sm text-gray-700">{row.criterion}</span>
        {row.example ? (
          <span className="self-start mt-1 text-xs text-gray-600 bg-[#F2F4F7] rounded-md px-2 py-1">
            {row.example}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
          {t("activityRecoGuide.thenLabel")}
        </span>
        <span className="text-sm text-gray-700">{row.recommendation}</span>
        <span className="text-sm text-gray-500">{row.impact}</span>
      </div>
    </div>
  </div>
);

const ThemeSection = ({ theme, rows, thresholds, t }) => (
  <section className="flex flex-col gap-3">
    <div className="flex flex-col gap-1">
      <h3 className="m-0 text-base font-bold text-[#0F265C]">
        {t(theme.titleKey)}
      </h3>
      <p className="m-0 text-sm text-gray-600">{t(theme.descriptionKey)}</p>
    </div>

    {(theme.thresholdKeys.length > 0 || theme.contaminantsBand) && (
      <div className="flex flex-wrap gap-2">
        {theme.thresholdKeys.map((item) => (
          <ThresholdPill key={item.labelKey}>
            {t(item.labelKey, { value: thresholds[item.valueKey] })}
          </ThresholdPill>
        ))}
        {theme.contaminantsBand ? (
          <ThresholdPill>
            {t("activityRecoGuide.thresholdContaminants", {
              watch: thresholds.CONTAMINANT_WATCH,
              medium: thresholds.CONTAMINANT_MEDIUM,
              high: thresholds.CONTAMINANT_HIGH,
            })}
          </ThresholdPill>
        ) : null}
      </div>
    )}

    <div className="flex flex-col gap-3">
      {rows.map((row) => (
        <RecommendationTypeCard key={row.id} row={row} t={t} />
      ))}
    </div>
  </section>
);

const ActivityRecommendationsGuide = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const thresholds = ACTIVITY_RECOMMENDATION_THRESHOLDS;

  const catalogRows = useMemo(
    () =>
      ACTIVITY_RECOMMENDATION_CATALOG.map((entry) => ({
        ...entry,
        scenarioTitle: t(entry.scenarioTitleKey),
        scenarioDescription: t(entry.scenarioDescriptionKey),
        criterion: t(entry.criterionKey, thresholds),
        example: t(entry.exampleKey, thresholds),
        recommendation: t(entry.recommendationKey, thresholds),
        impact: t(entry.impactKey, thresholds),
      })),
    [t, thresholds],
  );

  const themeSections = useMemo(
    () =>
      THEMES.map((theme) => ({
        theme,
        rows: catalogRows.filter((row) => theme.scopes.includes(row.scope)),
      })).filter((section) => section.rows.length > 0),
    [catalogRows],
  );

  return (
    <div className="settings-list-shell">
      <PageHeader
        showBackButton
        onBackClick={() => history.goBack()}
        titleContent={
          <span style={{ fontSize: "1.25rem" }} className="font-semibold">
            {t("Recommendations guide")}
          </span>
        }
      />
      <div className="settings-content-wrapper flex flex-col gap-8 max-w-4xl mx-auto w-full">
        <article className="bg-white border border-gray-300 rounded-xl p-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 text-base font-bold text-[#222222]">
              {t("activityRecoGuide.introTitle")}
            </h2>
            <p className="m-0 text-sm text-gray-700 leading-relaxed">
              {t("activityRecoGuide.introBody")}
            </p>
          </div>
          <ol className="m-0 pl-5 flex flex-col gap-2 text-sm text-gray-700 leading-relaxed">
            <li>{t("activityRecoGuide.stepObserve")}</li>
            <li>{t("activityRecoGuide.stepScore")}</li>
            <li>{t("activityRecoGuide.stepAct")}</li>
          </ol>
        </article>

        <article className="bg-white border border-gray-300 rounded-xl p-5 flex flex-col gap-3">
          <h2 className="m-0 text-base font-bold text-[#222222]">
            {t("activityRecoGuide.impactLevelsTitle")}
          </h2>
          <div className="flex flex-col gap-2">
            {["high", "medium", "watch"].map((severity) => (
              <div key={severity} className="flex items-start gap-3">
                <ImpactBadge severity={severity} t={t} />
                <span className="text-sm text-gray-700 pt-0.5">
                  {t(`activityRecoGuide.impactLevels.${severity}`)}
                </span>
              </div>
            ))}
          </div>
        </article>

        {themeSections.map(({ theme, rows }) => (
          <ThemeSection
            key={theme.id}
            theme={theme}
            rows={rows}
            thresholds={thresholds}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default ActivityRecommendationsGuide;
